import React, { useEffect, useRef, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { insertGame } from "../../modules/adminApi";
import { ACCENT, BORDER_STRONG, DANGER, INSET_BG, MUTED } from "./settingsPalette";

const ACCEPTED = "image/png,image/jpeg,image/webp,image/gif";
const useStyles = makeStyles(() => ({
  field: { width: "100%", marginBottom: "1rem" },
  hint: { fontSize: "0.75rem", color: MUTED, marginBottom: "0.75rem" },
  error: { fontSize: "0.75rem", color: DANGER },
  preview: { width: "64px", height: "64px", objectFit: "contain", marginRight: "1rem" },
  logoRow: {
    display: "flex", alignItems: "center", marginBottom: "1rem", padding: "1rem",
    border: `2px dashed ${BORDER_STRONG}`, borderRadius: "4px",
  },
  dropActive: { borderColor: ACCENT, backgroundColor: INSET_BG },
  dropHint: { fontSize: "0.75rem", color: MUTED },
  fileInput: { display: "none" },
}));

export default function DialogGameCreate({ isOpen, onClose, onCreated, createSnackbar }) {
  const classes = useStyles();
  const [title, setTitle] = useState("");
  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);
  const logoInput = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setTitle("");
    setLogo(null);
    setPreview(null);
    setError("");
    setIsDragging(false);
    dragDepth.current = 0;
  }, [isOpen]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const selectLogo = (file) => {
    if (isSaving || !file) return;
    if (!ACCEPTED.split(",").includes(file.type)) return setError("PNG, JPEG, WEBP or GIF only.");
    if (file.size > 2 * 1024 * 1024) return setError("The logo must be under 2MB.");
    setLogo(file);
    setPreview(URL.createObjectURL(file));
    setError("");
  };

  const pickLogo = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    selectLogo(file);
  };

  const dropLogo = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setIsDragging(false);
    if (isSaving) return;
    const files = event.dataTransfer.files;
    if (files.length > 1) return setError("Choose one image for the game logo.");
    selectLogo(files[0]);
  };

  const submit = async () => {
    setIsSaving(true);
    const result = await insertGame(title.trim(), logo);
    setIsSaving(false);
    if (!result.ok) {
      const message = typeof result.data === "string" ? result.data : result.status;
      setError(`Could not create the game: ${message}`);
      return;
    }
    createSnackbar(`${title.trim()} created. Add a base to make it available to a company.`, "success", false);
    onCreated(result.data);
  };

  return (
    <Dialog open={Boolean(isOpen)} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New playEngine game</DialogTitle>
      <DialogContent>
        <Typography className={classes.hint}>Create a game immediately, then add a base under it.</Typography>
        <TextField
          className={classes.field}
          size="small"
          label="Game title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          inputProps={{ maxLength: 120 }}
        />
        <Typography className={classes.hint}>Image logo</Typography>
        <div
          className={`${classes.logoRow} ${isDragging ? classes.dropActive : ""}`}
          onDragEnter={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (isSaving) return;
            dragDepth.current += 1;
            setIsDragging(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = isSaving ? "none" : "copy";
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            dragDepth.current = Math.max(0, dragDepth.current - 1);
            if (dragDepth.current === 0) setIsDragging(false);
          }}
          onDrop={dropLogo}
        >
          {preview && <img className={classes.preview} src={preview} alt="Selected game logo" draggable={false} />}
          <input ref={logoInput} id="runtimeGameLogoInput" className={classes.fileInput} type="file" accept={ACCEPTED} onChange={pickLogo} disabled={isSaving} />
          <div>
            <Typography className={classes.dropHint}>{isDragging ? "Drop your logo here" : "Drag and drop your logo here, or"}</Typography>
            <Button size="small" disabled={isSaving} onClick={() => logoInput.current && logoInput.current.click()}>Choose logo</Button>
          </div>
        </div>
        <Typography className={classes.hint}>PNG, JPEG, WEBP or GIF · up to 2MB</Typography>
        {error && <Typography className={classes.error}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={submit} disabled={isSaving || !title.trim() || !logo}>Create game</Button>
      </DialogActions>
    </Dialog>
  );
}
